import { useState, useEffect } from 'react';
import {
    Container,
    Box,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Avatar,
    Chip,
    TextField,
    InputAdornment,
    CircularProgress,
    TablePagination,
    IconButton,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Grid,
    Divider
} from '@mui/material';
import {
    People as PeopleIcon,
    Search as SearchIcon,
    CheckCircle as CheckCircleIcon,
    Visibility as VisibilityIcon,
    Close as CloseIcon
} from '@mui/icons-material';
import adminService from '../services/adminService';
import { formatTimeAgo } from '../utils/timeUtils';
import '../styles/ProtectedRoutes.css';

const AdminCustomers = () => {
    const [customers, setCustomers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [page, setPage] = useState(0);
    const [rowsPerPage] = useState(10);
    const [viewDialogOpen, setViewDialogOpen] = useState(false);
    const [selectedCustomer, setSelectedCustomer] = useState(null);

    useEffect(() => {
        loadCustomers();
    }, []);

    const loadCustomers = async () => {
        try {
            setLoading(true);
            const response = await adminService.getCustomers();

            if (response.success) {
                setCustomers(response.data);
            }
        } catch (error) {
            console.error('Error loading customers:', error);
        } finally {
            setLoading(false);
        }
    };

    const filteredCustomers = customers.filter(customer =>
        customer.CustomerName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        customer.EmailID?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        customer.PhoneNumber?.includes(searchTerm)
    );

    // Pagination
    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    // Reset to first page when search term changes
    useEffect(() => {
        setPage(0);
    }, [searchTerm]);

    // Get paginated customers
    const paginatedCustomers = filteredCustomers.slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage
    );

    const handleView = (customer) => {
        setSelectedCustomer(customer);
        setViewDialogOpen(true);
    };

    const handleCloseView = () => {
        setViewDialogOpen(false);
        setSelectedCustomer(null);
    };

    if (loading) {
        return (
            <Container maxWidth="lg" sx={{ py: 4 }}>
                <Box display="flex" flexDirection="column" alignItems="center" gap={2} className="glass-content-container">
                    <CircularProgress size={40} className="glass-loading" />
                    <Typography variant="body1" className="glass-text-secondary">
                        Loading customers...
                    </Typography>
                </Box>
            </Container>
        );
    }

    return (
        <Container maxWidth="lg" sx={{ py: { xs: 2, sm: 3, md: 4 } }}>
            {/* Header */}
            <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PeopleIcon sx={{ color: '#bc965e', fontSize: { xs: 28, sm: 32 } }} />
                    <Box>
                        <Typography variant="h4" className="glass-title" sx={{ fontSize: { xs: '1.5rem', sm: '2rem' } }}>
                            Customer Management
                        </Typography>
                        <Typography variant="body2" className="glass-text-secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                            {filteredCustomers.length} registered customer{filteredCustomers.length !== 1 ? 's' : ''}
                        </Typography>
                    </Box>
                </Box>
            </Box>

            {/* Search */}
            <Paper elevation={1} className="glass-content-container" sx={{ p: 0, overflow: 'hidden' }}>
                <TextField
                    placeholder="Search customers..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    variant="outlined"
                    size="small"
                    fullWidth
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            backgroundColor: 'rgba(255, 255, 255, 0.05)',
                            '& fieldset': {
                                borderColor: 'rgba(188, 150, 94, 0.3)',
                            },
                            '&:hover fieldset': {
                                borderColor: 'rgba(188, 150, 94, 0.5)',
                            },
                            '&.Mui-focused fieldset': {
                                borderColor: '#bc965e',
                            },
                        },
                        '& .MuiInputBase-input': {
                            color: '#ffffff',
                        },
                        '& .MuiInputBase-input::placeholder': {
                            color: 'rgba(255, 255, 255, 0.7)',
                            opacity: 1,
                        },
                    }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon sx={{ color: '#bc965e' }} />
                            </InputAdornment>
                        ),
                    }}
                />
            </Paper>

            {/* Customers Table */}
            <Paper elevation={1} className="glass-content-container" sx={{ overflow: 'hidden' }}>
                <TableContainer
                    className="admin-customers-table-wrapper"
                    sx={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch', display: 'block', width: '100%' }}
                >
                    <Table sx={{ minWidth: 900 }}>
                        <TableHead>
                            <TableRow sx={{
                                backgroundColor: 'rgba(188, 150, 94, 0.15)',
                                '& th': {
                                    borderBottom: '2px solid rgba(188, 150, 94, 0.3)'
                                }
                            }}>
                                <TableCell
                                    sx={{
                                        color: '#bc965e',
                                        fontWeight: 700,
                                        fontSize: { xs: '0.75rem', sm: '0.85rem', md: '0.95rem' },
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.5px',
                                        py: { xs: 0.8, sm: 1 }
                                    }}
                                >
                                    Customer
                                </TableCell>
                                <TableCell
                                    sx={{
                                        color: '#bc965e',
                                        fontWeight: 700,
                                        fontSize: { xs: '0.75rem', sm: '0.85rem', md: '0.95rem' },
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.5px',
                                        py: { xs: 0.8, sm: 1 }
                                    }}
                                >
                                    Email
                                </TableCell>
                                <TableCell
                                    sx={{
                                        color: '#bc965e',
                                        fontWeight: 700,
                                        fontSize: { xs: '0.75rem', sm: '0.85rem', md: '0.95rem' },
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.5px',
                                        py: { xs: 0.8, sm: 1 },

                                    }}
                                >
                                    Phone
                                </TableCell>
                                <TableCell
                                    sx={{
                                        color: '#bc965e',
                                        fontWeight: 700,
                                        fontSize: { xs: '0.75rem', sm: '0.85rem', md: '0.95rem' },
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.5px',
                                        py: { xs: 0.8, sm: 1 }
                                    }}
                                >
                                    Join Date
                                </TableCell>
                                <TableCell
                                    sx={{
                                        color: '#bc965e',
                                        fontWeight: 700,
                                        fontSize: { xs: '0.75rem', sm: '0.85rem', md: '0.95rem' },
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.5px',
                                        py: { xs: 0.8, sm: 1 }
                                    }}
                                >
                                    Status
                                </TableCell>
                                <TableCell
                                    align="center"
                                    sx={{
                                        color: '#bc965e',
                                        fontWeight: 700,
                                        fontSize: { xs: '0.75rem', sm: '0.85rem', md: '0.95rem' },
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.5px',
                                        py: { xs: 0.8, sm: 1 }
                                    }}
                                >
                                    Actions
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {paginatedCustomers.map((customer) => (
                                <TableRow
                                    key={customer.TokenNo}
                                    hover
                                    sx={{
                                        '&:hover': {
                                            backgroundColor: 'rgba(188, 150, 94, 0.05)',
                                        },
                                        '& td': {
                                            borderBottom: '1px solid rgba(188, 150, 94, 0.1)'
                                        }
                                    }}
                                >
                                    <TableCell sx={{ py: { xs: 0.8, sm: 1 } }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, sm: 1 } }}>
                                            <Avatar
                                                className="glass-avatar"
                                                sx={{
                                                    width: { xs: 32, sm: 40 },
                                                    height: { xs: 32, sm: 40 },
                                                    background: 'linear-gradient(135deg, #bc965e, #a0804d)',
                                                    fontWeight: 600,
                                                    fontSize: { xs: '0.875rem', sm: '1rem' }
                                                }}
                                            >
                                                {customer.CustomerName?.charAt(0) || 'C'}
                                            </Avatar>
                                            <Typography
                                                variant="body2"
                                                className="glass-text-primary"
                                                sx={{
                                                    fontWeight: 500,
                                                    fontSize: { xs: '0.8rem', sm: '0.9rem' }
                                                }}
                                            >
                                                {customer.CustomerName}
                                            </Typography>
                                        </Box>
                                    </TableCell>
                                    <TableCell sx={{ py: { xs: 0.8, sm: 1 } }}>
                                        <Typography
                                            variant="body2"
                                            className="glass-text-primary"
                                            sx={{ fontSize: { xs: '0.75rem', sm: '0.8rem', md: '0.875rem' } }}
                                        >
                                            {customer.EmailID}
                                        </Typography>
                                    </TableCell>
                                    <TableCell sx={{ py: { xs: 0.8, sm: 1 }, }}>
                                        <Typography
                                            variant="body2"
                                            className="glass-text-primary"
                                            sx={{ fontSize: '0.875rem' }}
                                        >
                                            {customer.PhoneNumber || 'N/A'}
                                        </Typography>
                                    </TableCell>
                                    <TableCell sx={{ py: { xs: 0.8, sm: 1 } }}>
                                        <Typography
                                            variant="body2"
                                            className="glass-text-secondary"
                                            sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                                        >
                                            {formatTimeAgo(customer.JoinDate)}
                                        </Typography>
                                    </TableCell>
                                    <TableCell sx={{ py: { xs: 0.8, sm: 1 } }}>
                                        <Chip
                                            label="Active"
                                            color="success"
                                            size="small"
                                            icon={<CheckCircleIcon sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }} />}
                                            sx={{
                                                fontWeight: 500,
                                                fontSize: { xs: '0.65rem', sm: '0.75rem' },
                                                height: { xs: 22, sm: 28 },
                                                '& .MuiChip-label': {
                                                    px: { xs: 0.75, sm: 1 }
                                                }
                                            }}
                                        />
                                    </TableCell>
                                    <TableCell align="center" sx={{ py: { xs: 0.8, sm: 1 } }}>
                                        <Tooltip title="View">
                                            <IconButton
                                                size="small"
                                                onClick={() => handleView(customer)}
                                                sx={{
                                                    color: '#4a90e2',
                                                    p: { xs: 0.5, sm: 0.75 },
                                                    '&:hover': {
                                                        backgroundColor: 'rgba(74, 144, 226, 0.1)',
                                                    }
                                                }}
                                            >
                                                <VisibilityIcon sx={{ fontSize: { xs: '1rem', sm: '1.15rem' } }} />
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>

                {filteredCustomers.length === 0 ? (
                    <Box sx={{ p: 4, textAlign: 'center' }}>
                        <Typography variant="body1" className="glass-text-secondary">
                            No customers found matching your search.
                        </Typography>
                    </Box>
                ) : (
                    <TablePagination
                        component="div"
                        count={filteredCustomers.length}
                        page={page}
                        onPageChange={handleChangePage}
                        rowsPerPage={rowsPerPage}
                        rowsPerPageOptions={[10]}
                        sx={{
                            borderTop: '1px solid rgba(188, 150, 94, 0.2)',
                            '& .MuiTablePagination-toolbar': {
                                color: '#ffffff',
                                minHeight: { xs: 52, sm: 64 },
                                px: { xs: 1, sm: 2 }
                            },
                            '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                                color: 'rgba(255, 255, 255, 0.7)',
                                fontSize: { xs: '0.75rem', sm: '0.875rem' }
                            },
                            '& .MuiTablePagination-select': {
                                color: '#ffffff',
                                fontSize: { xs: '0.75rem', sm: '0.875rem' }
                            },
                            '& .MuiTablePagination-actions button': {
                                color: '#bc965e',
                                p: { xs: 0.5, sm: 1 }
                            },
                        }}
                    />
                )}
            </Paper>

            {/* View Customer Dialog */}
  
            <Dialog
                open={viewDialogOpen}
                onClose={handleCloseView}
                maxWidth="xs"
                fullWidth
                PaperProps={{
                    sx: {
                        background: 'linear-gradient(160deg, #1a1208 0%, #0f0d0a 100%)',
                        border: '1px solid rgba(188, 150, 94, 0.73)',
                        borderRadius: 1.5,
                        overflow: 'hidden',
                        boxShadow: '0 32px 80px rgba(0,0,0,0.8)',
                        m: { xs: 2, sm: 3 },
                    }
                }}
            >
                {selectedCustomer && (
                    <>
                        {/* ── Header ── */}
                        <Box sx={{
                            px: 3, pt: 3, pb: 2.5,
                            borderBottom: '1px solid rgba(188, 150, 94, 0.52)',
                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Avatar sx={{
                                    width: 48, height: 48,
                                    background: 'linear-gradient(135deg, #bc965e 0%, #8a6a3a 100%)',
                                    fontSize: '1.25rem', fontWeight: 700,
                                    border: '1.5px solid rgba(188,150,94,0.35)',
                                }}>
                                    {selectedCustomer.CustomerName?.charAt(0)?.toUpperCase() || 'C'}
                                </Avatar>
                                <Box>
                                    <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '1rem', lineHeight: 1.3 }}>
                                        {selectedCustomer.CustomerName}
                                    </Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.4 }}>
                                        <Box sx={{ width: 6, height: 6, borderRadius: '50%', background: '#4caf50' }} />
                                        <Typography sx={{ color: '#4caf50', fontSize: '0.72rem', fontWeight: 600 }}>
                                            Active
                                        </Typography>
                                    </Box>
                                </Box>
                            </Box>
                            <IconButton onClick={handleCloseView} size="small" sx={{
                                color: '#ffffff',
                                '&:hover': { color: 'rgba(255,255,255,0.7)', background: 'rgba(255,255,255,0.06)' },
                            }}>
                                <CloseIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                        </Box>

                        {/* ── Info rows ── */}
                        <DialogContent sx={{ p: 0 }}>
                            {[
                                { icon: '✉', label: 'Email', value: selectedCustomer.EmailID, mono: true },
                                { icon: '📞', label: 'Phone', value: selectedCustomer.PhoneNumber || '—', mono: true },
                                { icon: '🎂', label: 'Age', value: selectedCustomer.Age ? `${selectedCustomer.Age} yrs` : '—' },
                                { icon: '💼', label: 'Profession', value: selectedCustomer.Profession === 'Other' ? selectedCustomer.ProfessionOther : selectedCustomer.Profession || '—' },
                                { icon: '📅', label: 'Member since', value: formatTimeAgo(selectedCustomer.JoinDate) },
                            ].map((row, i, arr) => (
                                <Box key={row.label} sx={{
                                    display: 'flex', alignItems: 'center',
                                    gap: 2, px: 3, py: 1.6,
                                    borderBottom: i < arr.length - 1
                                        ? '1px solid rgba(255,255,255,0.05)'
                                        : 'none',
                                    '&:hover': { background: 'rgba(188,150,94,0.04)' },
                                    transition: 'background 0.15s',
                                }}>
                                    {/* Label */}
                                    <Typography sx={{
                                        color: '#ffffff',
                                        fontSize: '0.7rem',
                                        fontWeight: 600,
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.08em',
                                        minWidth: 90,
                                        flexShrink: 0,
                                    }}>
                                        
                                        {row.label}
                                    </Typography>

                                    {/* Divider dot */}
                                    <Box sx={{ width: 3, height: 3, borderRadius: '50%', background: 'rgba(188, 150, 94, 0.76)', flexShrink: 0 }} />

                                    {/* Value */}
                                    <Typography sx={{
                                        color: '#ffffff',
                                        fontSize: '0.82rem',
                                        fontWeight: 500,
                                        fontFamily: row.mono ? '"Courier New", monospace' : 'inherit',
                                        wordBreak: 'break-all',
                                        flex: 1,
                                    }}>
                                        {row.value}
                                    </Typography>
                                </Box>
                            ))}
                        </DialogContent>

                        {/* ── Footer ── */}
                        <Box sx={{ px: 3, py: 2, borderTop: '1px solid rgba(188,150,94,0.12)', display: 'flex', gap: 1 }}>
                            <Button
                                onClick={handleCloseView}
                                fullWidth
                                sx={{
                                    border: '1px solid rgba(188, 150, 94, 0.66)',
                                    color: '#ffffff',
                                    borderRadius: 1,
                                    textTransform: 'none',
                                    fontSize: '0.82rem',
                                    py: 0.9,
                                    '&:hover': { border: '1px solid rgba(188,150,94,0.4)', color: '#bc965e', background: 'rgba(188,150,94,0.06)' },
                                }}
                            >
                                Close
                            </Button>
                        </Box>
                    </>
                )}
            </Dialog>


        </Container>
    );
};

export default AdminCustomers;
